import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Wordmark, Footer, Shield } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

// Full-screen gate shown to a founder who has not accepted the current terms version.
export default function TermsGate({ children }) {
  const { user, loadMe } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || !user.needs_terms_acceptance) return children;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/legal/accept");
      await loadMe();
      toast.success("Thanks — you're all set.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-wr-bg flex flex-col wr-grid-bg" data-testid="terms-gate">
      <header className="border-b border-wr-border bg-black/70 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 h-16 flex items-center">
          <Wordmark size="md" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg bg-wr-surface border border-wr-border p-7 sm:p-9 wr-fade-up">
          <Shield className="h-11 w-11 mb-4" />
          <p className="wr-label !text-wr-red flex items-center gap-2"><ShieldCheck size={13} /> BEFORE YOU BEGIN</p>
          <h1 className="wr-heading font-bold text-white text-2xl mt-3">Review &amp; accept the terms.</h1>
          <p className="text-sm text-wr-sub mt-2">
            Please review these documents before continuing. Your acceptance is recorded.
          </p>
          <div className="mt-5 flex flex-col gap-2" data-testid="terms-gate-links">
            {[["terms", "Terms of Service"], ["privacy", "Privacy Policy"], ["ai_use", "AI Use Disclosure"]].map(([d, label]) => (
              <Link
                key={d}
                to={`/legal/${d}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`terms-gate-link-${d}`}
                className="wr-mono text-sm text-wr-sub hover:text-white border-l-2 border-wr-border hover:border-wr-red pl-3 py-1.5 transition-colors"
              >
                {label} ↗
              </Link>
            ))}
          </div>
          <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="terms-gate-checkbox-label">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              data-testid="terms-gate-checkbox"
              className="mt-0.5 rounded-none border-wr-border data-[state=checked]:bg-wr-red data-[state=checked]:border-wr-red"
            />
            <span className="text-sm text-wr-sub">
              I have read and accept the Terms of Service, Privacy Policy, and AI Use Disclosure.
            </span>
          </label>
          <Button
            onClick={accept}
            disabled={!agreed || busy}
            data-testid="terms-gate-accept"
            className="mt-6 w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11 disabled:opacity-50"
          >
            {busy ? "Recording…" : "Accept & continue"}
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
